import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Button,
    Dialog,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
} from "@material-ui/core";
import { Pipette, Copy as CopyIcon, X as LucideX, Check } from "lucide-react";
import { i18n } from "../../translate/i18n";
import AppTooltip from "../AppTooltip";

const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

// ---------- Conversões de cor ----------
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const hexToRgb = (hex) => {
    let h = (hex || "").replace("#", "");
    if (h.length === 3) {
        h = h.split("").map((c) => c + c).join("");
    }
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
    return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    };
};

const rgbToHex = ({ r, g, b }) => {
    const toHex = (v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const rgbToHsv = ({ r, g, b }) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    const s = max === 0 ? 0 : d / max;
    const v = max;
    if (d !== 0) {
        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
                break;
            case g:
                h = ((b - r) / d + 2) * 60;
                break;
            default:
                h = ((r - g) / d + 4) * 60;
        }
    }
    return { h, s: s * 100, v: v * 100 };
};

const hsvToRgb = ({ h, s, v }) => {
    s /= 100;
    v /= 100;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    return {
        r: (r + m) * 255,
        g: (g + m) * 255,
        b: (b + m) * 255,
    };
};

const hsvToHex = (hsv) => rgbToHex(hsvToRgb(hsv));

const hexToHsv = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    return rgbToHsv(rgb);
};

// ---------- Estilos ----------
const useStyles = makeStyles((theme) => {
    const ds = theme.chatiaDS || {};
    const dsColors = ds.colors || {};
    const isDark = theme.palette.type === "dark";
    return {
        dialogPaper: {
            borderRadius: "20px !important",
            background: isDark ? "#121212" : "#ffffff",
            overflow: "hidden",
            boxShadow: isDark
                ? "0 20px 60px rgba(0,0,0,0.6)"
                : "0 20px 60px rgba(15,23,42,0.18)",
        },
        header: {
            ...(ds.header || {}),
            flexShrink: 0,
        },
        headerTitle: {
            ...(ds.headerTitle || {}),
        },
        closeButton: {
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "#ffffff",
            transition: "all 0.2s ease",
        },
        body: {
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 14,
        },

        // Saturation/Value box
        satBox: {
            position: "relative",
            width: "100%",
            height: 180,
            borderRadius: 10,
            cursor: "crosshair",
            userSelect: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            overflow: "hidden",
        },
        satCursor: {
            position: "absolute",
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "2px solid #fff",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(0,0,0,0.4)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
        },

        // Hue slider
        hueSlider: {
            position: "relative",
            width: "100%",
            height: 14,
            borderRadius: 8,
            background:
                "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            cursor: "pointer",
            userSelect: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
        },
        hueCursor: {
            position: "absolute",
            top: "50%",
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            border: "2px solid #fff",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.3)",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
        },

        // Eyedropper button
        eyeDropperBtn: {
            borderRadius: 12,
            textTransform: "none",
            gap: 8,
            padding: "10px 16px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            background: isDark ? "rgba(255,255,255,0.03)" : "transparent",
            color: dsColors.textPrimary || (isDark ? "#e9edef" : "#0f172a"),
            fontWeight: 500,
            fontSize: 13,
            transition: "all 0.18s ease",
            "&:hover": {
                background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
                borderColor: theme.palette.primary.main,
            },
        },

        // Preview row (hex input + swatch)
        previewRow: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: 10,
            borderRadius: 12,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
        },
        colorPreview: {
            width: 36,
            height: 36,
            borderRadius: 10,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            flexShrink: 0,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
        },
        hexInput: {
            flex: 1,
            "& .MuiOutlinedInput-root": {
                borderRadius: 10,
                background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
            },
            "& input": {
                fontFamily: "monospace",
                fontSize: 14,
                fontWeight: 500,
                textTransform: "uppercase",
                color: dsColors.textPrimary || (isDark ? "#e9edef" : "#0f172a"),
            },
        },

        // RGB inputs
        rgbRow: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
        },
        rgbInput: {
            "& .MuiOutlinedInput-root": {
                borderRadius: 10,
                background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
            },
            "& input": {
                fontFamily: "monospace",
                fontSize: 13,
                fontWeight: 500,
                textAlign: "center",
                color: dsColors.textPrimary || (isDark ? "#e9edef" : "#0f172a"),
            },
            "& .MuiInputLabel-root": {
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.5,
            },
        },

        // Footer actions
        actions: {
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "0 16px 16px 16px",
        },
        cancelBtn: {
            borderRadius: 12,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 500,
            padding: "8px 18px",
            color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        },
        okBtn: {
            borderRadius: 12,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            padding: "8px 22px",
            background: theme.palette.primary.main,
            color: "#ffffff",
            "&:hover": {
                background: theme.palette.primary.dark || theme.palette.primary.main,
                filter: "brightness(1.05)",
            },
        },
    };
});

// ---------- Componente ----------
const ColorBoxModal = ({ onChange, currentColor, handleClose, open }) => {
    const classes = useStyles();
    const [hsv, setHsv] = useState({ h: 0, s: 0, v: 0 });
    const [hexInput, setHexInput] = useState("#000000");
    const [copied, setCopied] = useState(false);
    const [picking, setPicking] = useState(false);

    const satRef = useRef(null);
    const hueRef = useRef(null);
    const draggingRef = useRef(null); // 'sat' | 'hue' | null

    // Inicializa com a cor recebida
    useEffect(() => {
        if (!open) return;
        const start = currentColor || "#000000";
        const parsed = hexToHsv(start);
        if (parsed) {
            setHsv(parsed);
            setHexInput(start.toUpperCase());
        }
    }, [open, currentColor]);

    const hex = hsvToHex(hsv);
    const rgb = hsvToRgb(hsv);

    const updateFromHsv = (next) => {
        const safe = {
            h: clamp(next.h, 0, 360),
            s: clamp(next.s, 0, 100),
            v: clamp(next.v, 0, 100),
        };
        setHsv(safe);
        setHexInput(hsvToHex(safe));
    };

    // ---------- Saturation/Value handlers ----------
    const handleSatPointer = useCallback((clientX, clientY) => {
        const el = satRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = clamp(clientX - rect.left, 0, rect.width);
        const y = clamp(clientY - rect.top, 0, rect.height);
        const s = (x / rect.width) * 100;
        const v = 100 - (y / rect.height) * 100;
        setHsv((prev) => {
            const next = { h: prev.h, s, v };
            setHexInput(hsvToHex(next));
            return next;
        });
    }, []);

    const handleSatMouseDown = (e) => {
        draggingRef.current = "sat";
        handleSatPointer(e.clientX, e.clientY);
    };

    // ---------- Hue handlers ----------
    const handleHuePointer = useCallback((clientX) => {
        const el = hueRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = clamp(clientX - rect.left, 0, rect.width);
        const h = (x / rect.width) * 360;
        setHsv((prev) => {
            const next = { ...prev, h };
            setHexInput(hsvToHex(next));
            return next;
        });
    }, []);

    const handleHueMouseDown = (e) => {
        draggingRef.current = "hue";
        handleHuePointer(e.clientX);
    };

    // ---------- Global drag listeners ----------
    useEffect(() => {
        const onMove = (e) => {
            if (!draggingRef.current) return;
            if (draggingRef.current === "sat") handleSatPointer(e.clientX, e.clientY);
            else if (draggingRef.current === "hue") handleHuePointer(e.clientX);
        };
        const onUp = () => {
            draggingRef.current = null;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [handleSatPointer, handleHuePointer]);

    // ---------- Hex input ----------
    const handleHexChange = (e) => {
        let val = e.target.value.toUpperCase();
        if (!val.startsWith("#")) val = `#${val}`;
        if (!/^#[0-9A-F]{0,6}$/.test(val)) return;
        setHexInput(val);
        if (val.length === 7) {
            const parsed = hexToHsv(val);
            if (parsed) setHsv(parsed);
        }
    };

    const handleRgbChange = (channel) => (e) => {
        const num = clamp(parseInt(e.target.value || "0", 10) || 0, 0, 255);
        const nextRgb = { ...rgb, [channel]: num };
        const nextHex = rgbToHex(nextRgb);
        const parsed = hexToHsv(nextHex);
        if (parsed) {
            setHsv(parsed);
            setHexInput(nextHex);
        }
    };

    // ---------- Eyedropper ----------
    const handleEyeDropper = async () => {
        if (!hasEyeDropper) return;
        try {
            setPicking(true);
            const eyeDropper = new window.EyeDropper();
            const result = await eyeDropper.open();
            const parsed = hexToHsv(result.sRGBHex);
            if (parsed) {
                setHsv(parsed);
                setHexInput(result.sRGBHex.toUpperCase());
            }
        } catch (e) {
            // user cancelled
        } finally {
            setPicking(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(hex).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const handleOk = () => {
        onChange(hex);
        handleClose();
    };

    const satBgGradient = `
        linear-gradient(to top, #000 0%, transparent 100%),
        linear-gradient(to right, #fff 0%, hsl(${hsv.h}, 100%, 50%) 100%)
    `;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            BackdropProps={{ style: { backgroundColor: picking ? "transparent" : undefined } }}
            PaperProps={{
                className: classes.dialogPaper,
                style: picking ? { visibility: "hidden" } : {},
            }}
            style={picking ? { visibility: "hidden" } : {}}
        >
            <div className={classes.header}>
                <Typography className={classes.headerTitle}>
                    {i18n.t("colorBoxModal.title")}
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    className={classes.closeButton}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                    }}
                >
                    <LucideX size={18} strokeWidth={2.5} />
                </IconButton>
            </div>

            <div className={classes.body}>
                {/* Saturation/Value */}
                <div
                    ref={satRef}
                    className={classes.satBox}
                    style={{ background: satBgGradient }}
                    onMouseDown={handleSatMouseDown}
                >
                    <div
                        className={classes.satCursor}
                        style={{
                            left: `${hsv.s}%`,
                            top: `${100 - hsv.v}%`,
                            background: hex,
                        }}
                    />
                </div>

                {/* Hue slider */}
                <div
                    ref={hueRef}
                    className={classes.hueSlider}
                    onMouseDown={handleHueMouseDown}
                >
                    <div
                        className={classes.hueCursor}
                        style={{
                            left: `${(hsv.h / 360) * 100}%`,
                            background: `hsl(${hsv.h}, 100%, 50%)`,
                        }}
                    />
                </div>

                {/* Hex */}
                <div className={classes.previewRow}>
                    <div
                        className={classes.colorPreview}
                        style={{ backgroundColor: hex }}
                    />
                    <TextField
                        className={classes.hexInput}
                        variant="outlined"
                        size="small"
                        label="HEX"
                        value={hexInput}
                        onChange={handleHexChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <AppTooltip
                                        titleKey={
                                            copied
                                                ? "colorBoxModal.tooltips.copied"
                                                : "colorBoxModal.tooltips.copy"
                                        }
                                    >
                                        <IconButton size="small" onClick={handleCopy}>
                                            {copied ? (
                                                <Check size={16} strokeWidth={2} color="#22c55e" />
                                            ) : (
                                                <CopyIcon size={16} strokeWidth={2} />
                                            )}
                                        </IconButton>
                                    </AppTooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                {/* RGB */}
                <div className={classes.rgbRow}>
                    <TextField
                        className={classes.rgbInput}
                        variant="outlined"
                        size="small"
                        label="R"
                        type="number"
                        inputProps={{ min: 0, max: 255 }}
                        value={Math.round(rgb.r)}
                        onChange={handleRgbChange("r")}
                    />
                    <TextField
                        className={classes.rgbInput}
                        variant="outlined"
                        size="small"
                        label="G"
                        type="number"
                        inputProps={{ min: 0, max: 255 }}
                        value={Math.round(rgb.g)}
                        onChange={handleRgbChange("g")}
                    />
                    <TextField
                        className={classes.rgbInput}
                        variant="outlined"
                        size="small"
                        label="B"
                        type="number"
                        inputProps={{ min: 0, max: 255 }}
                        value={Math.round(rgb.b)}
                        onChange={handleRgbChange("b")}
                    />
                </div>

                {hasEyeDropper && (
                    <Button
                        className={classes.eyeDropperBtn}
                        variant="outlined"
                        fullWidth
                        onClick={handleEyeDropper}
                        startIcon={<Pipette size={16} strokeWidth={2} />}
                    >
                        {i18n.t("colorBoxModal.eyeDropper") || "Capturar cor da tela"}
                    </Button>
                )}
            </div>

            <div className={classes.actions}>
                <Button onClick={handleClose} className={classes.cancelBtn}>
                    {i18n.t("colorBoxModal.buttons.cancel") || "Cancelar"}
                </Button>
                <Button
                    onClick={handleOk}
                    className={classes.okBtn}
                    variant="contained"
                    disableElevation
                >
                    {i18n.t("colorBoxModal.buttons.ok") || "OK"}
                </Button>
            </div>
        </Dialog>
    );
};

export default ColorBoxModal;
